import type { PersistZebraEventParams } from '@/entities/order/api/order.api'

const ZEBRA_EVENT_QUEUE_KEY = 'helloScreens:zebraEventQueue'

/** A queued event the backend refused outright, kept for support to inspect. */
export interface RejectedZebraEvent {
  event: PersistZebraEventParams
  reason: string
  rejectedAt: string
}

interface ZebraEventQueueSchema {
  version: 1
  events: PersistZebraEventParams[]
  rejected?: RejectedZebraEvent[]
}

const defaultQueueState: ZebraEventQueueSchema = {
  version: 1,
  events: [],
  rejected: [],
}

/** Rejected events are for diagnosis, not replay, so the newest few are enough. */
const MAX_REJECTED_EVENTS = 50

function isBrowser() {
  return typeof window !== 'undefined' && !!window.localStorage
}

function readQueue(): ZebraEventQueueSchema {
  if (!isBrowser()) {
    return defaultQueueState
  }
  try {
    const raw = window.localStorage.getItem(ZEBRA_EVENT_QUEUE_KEY)
    if (!raw) {
      return defaultQueueState
    }
    const parsed = JSON.parse(raw) as Partial<ZebraEventQueueSchema>
    if (parsed.version !== 1 || !Array.isArray(parsed.events)) {
      return defaultQueueState
    }
    return {
      version: 1,
      events: parsed.events,
      rejected: Array.isArray(parsed.rejected) ? parsed.rejected : [],
    }
  } catch {
    return defaultQueueState
  }
}

function writeQueue(state: ZebraEventQueueSchema) {
  if (!isBrowser()) {
    return
  }
  window.localStorage.setItem(ZEBRA_EVENT_QUEUE_KEY, JSON.stringify(state))
}

/**
 * Whether two entries describe the same event. Matches the backend's own idempotency:
 * a scan is identified by its barcode and a missing-crate report by its crate type, so
 * replaying one cannot create a second record.
 */
function isSameEvent(a: PersistZebraEventParams, b: PersistZebraEventParams): boolean {
  if (a.orderId !== b.orderId || a.eventType !== b.eventType) {
    return false
  }
  if (a.eventType === 'zebraScans' && b.eventType === 'zebraScans') {
    return a.barcode === b.barcode
  }
  if (a.eventType === 'missingCrates' && b.eventType === 'missingCrates') {
    return a.crateType === b.crateType
  }
  return false
}

function isDuplicate(existing: PersistZebraEventParams[], incoming: PersistZebraEventParams): boolean {
  return existing.some(event => isSameEvent(event, incoming))
}

export function enqueueZebraEvent(event: PersistZebraEventParams): void {
  const state = readQueue()
  if (isDuplicate(state.events, event)) {
    return
  }
  state.events.push(event)
  writeQueue(state)
}

/**
 * The queued events, left in place.
 *
 * Deliberately not a dequeue: an event stays queued until the backend has confirmed it,
 * so a replay that fails partway through - or a device that dies mid-flush - cannot
 * lose work that was never persisted (BD-8547).
 */
export function peekAllZebraEvents(): PersistZebraEventParams[] {
  return readQueue().events
}

/** Drop one event, by the same identity the backend deduplicates on. */
export function removeZebraEvent(event: PersistZebraEventParams): void {
  const state = readQueue()
  const nextEvents = state.events.filter(queued => !isSameEvent(queued, event))

  if (nextEvents.length === state.events.length) {
    return
  }

  writeQueue({ ...state, version: 1, events: nextEvents })
}

/**
 * Set aside an event the backend refused outright. It is off the replay queue, so it
 * cannot block the events behind it, but it is still recorded for support rather than
 * vanishing into a console line.
 */
export function recordRejectedZebraEvent(event: PersistZebraEventParams, reason: string): void {
  const state = readQueue()
  const rejected = [
    ...(state.rejected ?? []),
    { event, reason, rejectedAt: new Date().toISOString() },
  ].slice(-MAX_REJECTED_EVENTS)

  writeQueue({ ...state, version: 1, rejected })
}

export function listRejectedZebraEvents(): RejectedZebraEvent[] {
  return readQueue().rejected ?? []
}

export function clearZebraEventQueue(): void {
  writeQueue({ version: 1, events: [], rejected: [] })
}

export function removeZebraEventsForOrder(orderId: string): void {
  const state = readQueue()
  const nextEvents = state.events.filter(event => event.orderId !== orderId)

  if (nextEvents.length === state.events.length) {
    return
  }

  writeQueue({ ...state, version: 1, events: nextEvents })
}
